/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsRouteFormView.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Route create/edit form - following Extensions module pattern
    with 4-column inline layout.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Route, Plus, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { ROUTES } from '../../../constants/routes';
import { ModuleFormHeader } from '../../../components/layouts';
import { FixedActionBar } from '../../../components/ui/FixedActionBar';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Toggle } from '../../../components/ui/Toggle';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Select, { SelectOption } from '../../../components/ui/select';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SmsRouteFormData, SmsProvider } from '../types';
import { fetchRoute, fetchProviders, createRoute, updateRoute, deleteRoute } from '../api';

const createDefaultFormData = (): SmsRouteFormData => ({
    name: '',
    prefixPattern: '',
    countryCode: '',
    providerId: 0,
    priority: 100,
    enabled: true,
});

export default function SmsRouteFormView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const isEditMode = id && id !== 'new';

    // Form state
    const [formData, setFormData] = useState<SmsRouteFormData>(createDefaultFormData);
    const [originalFormData, setOriginalFormData] = useState<SmsRouteFormData>(createDefaultFormData);
    const [providers, setProviders] = useState<SmsProvider[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Computed values
    const hasChanges = useMemo(() =>
        JSON.stringify(formData) !== JSON.stringify(originalFormData),
        [formData, originalFormData]
    );

    // Provider options
    const providerOptions = useMemo((): SelectOption[] => {
        return providers.filter(p => p.enabled).map(p => ({
            value: p.id.toString(),
            label: p.name
        }));
    }, [providers]);

    const fallbackProviderOptions = useMemo((): SelectOption[] => {
        return [
            { value: '0', label: t('sms.routes.noFallback') },
            ...providers.filter(p => p.enabled).map(p => ({
                value: p.id.toString(),
                label: p.name
            }))
        ];
    }, [providers, t]);

    // Load data
    useEffect(() => {
        loadProviders();
        if (isEditMode && id) {
            loadRoute();
        }
    }, [id]);

    const loadProviders = async () => {
        try {
            const data = await fetchProviders();
            setProviders(data);
        } catch (error) {
            console.error('Failed to load providers:', error);
        }
    };

    const loadRoute = async () => {
        try {
            setIsLoading(true);
            const route = await fetchRoute(id!);
            if (route) {
                const data: SmsRouteFormData = {
                    name: route.name,
                    prefixPattern: route.prefixPattern || '',
                    countryCode: route.countryCode || '',
                    providerId: route.providerId,
                    priority: route.priority,
                    fallbackProviderId: route.fallbackProviderId,
                    enabled: route.enabled,
                };
                setFormData(data);
                setOriginalFormData(data);
            }
        } catch (error) {
            showError(error.message || t('sms.routes.messages.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    // Field handlers
    const handleFieldChange = useCallback((field: keyof SmsRouteFormData, value: unknown) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    }, [errors]);

    // Validation
    const validateForm = useCallback(() => {
        const newErrors: Record<string, string> = {};

        if (!formData.name?.trim()) {
            newErrors.name = t('validation.required');
        }

        if (!formData.providerId) {
            newErrors.providerId = t('validation.required');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, t]);

    // Save handler
    const handleSave = useCallback(async () => {
        if (!validateForm()) return undefined;

        try {
            setIsSaving(true);
            let createdId: string | number | undefined;
            if (isEditMode) {
                await updateRoute(id!, formData);
                showSuccess(t('sms.routes.messages.updateSuccess'));
            } else {
                const res = await createRoute(formData);
                createdId = (res as any)?.uuid ?? (res as any)?.id;
                showSuccess(t('sms.routes.messages.createSuccess'));
            }
            setOriginalFormData(formData);
            return { success: true, createdId };
        } catch (error) {
            showError(error.message || t('sms.routes.messages.saveError'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [formData, isEditMode, id, validateForm, showSuccess, showError, t]);

    // Action bar handlers
    const handleSaveWithAction = useCallback(async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        const result = await handleSave();
        if (result?.success) {
            if (action === 'save' && !isEditMode && result.createdId) {
                navigate(ROUTES.SMS_ROUTE_DETAIL(result.createdId));
            } else if (action === 'save-and-close') {
                navigate(ROUTES.SMS_ROUTES);
            } else if (action === 'save-and-new') {
                setFormData(createDefaultFormData());
                setOriginalFormData(createDefaultFormData());
                navigate(ROUTES.SMS_ROUTES_NEW);
            }
        }
    }, [handleSave, navigate, isEditMode]);

    const handleCancel = useCallback(() => {
        if (hasChanges) {
            setFormData(originalFormData);
        } else {
            navigate(ROUTES.SMS_ROUTES);
        }
    }, [hasChanges, originalFormData, navigate]);

    const handleDelete = useCallback(async () => {
        if (!isEditMode) return;

        try {
            setIsDeleting(true);
            await deleteRoute(id!);
            showSuccess(t('sms.routes.messages.deleteSuccess'));
            navigate(ROUTES.SMS_ROUTES);
        } catch (error) {
            showError(error.message || t('sms.routes.messages.deleteError'));
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [isEditMode, id, showSuccess, showError, navigate, t]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden p-6 pb-0">
            {/* Header */}
            <ModuleFormHeader
                title={isEditMode ? formData.name || t('sms.routes.editTitle') : t('sms.routes.createTitle')}
                icon={<Route className="h-5 w-5 text-primary" />}
                backButtonText={t('common.list', 'List')}
                onBack={() => navigate(ROUTES.SMS_ROUTES)}
            />

            {/* Scrollable Form Content */}
            <div className="flex-1 overflow-auto pb-24 mt-8">
                <FormBox
                    title={t('sms.routes.title')}
                    icon={<Route className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        {/* Row 1: Name | Prefix Pattern */}
                        <SimpleTooltip content={t('sms.routes.fields.name')} position="top">
                            <Label htmlFor="name" required className="text-left">
                                {t('sms.routes.fields.name')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleFieldChange('name', e.target.value)}
                            placeholder={t('sms.routes.fields.namePlaceholder')}
                            error={errors.name}
                        />
                        <SimpleTooltip content={t('sms.routes.fields.prefixPattern')} position="top">
                            <Label htmlFor="prefixPattern" className="text-left">
                                {t('sms.routes.fields.prefixPattern')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="prefixPattern"
                            value={formData.prefixPattern || ''}
                            onChange={(e) => handleFieldChange('prefixPattern', e.target.value)}
                            placeholder="+1505"
                        />

                        {/* Row 2: Country Code | Priority */}
                        <SimpleTooltip content={t('sms.routes.fields.countryCode')} position="top">
                            <Label htmlFor="countryCode" className="text-left">
                                {t('sms.routes.fields.countryCode')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="countryCode"
                            value={formData.countryCode || ''}
                            onChange={(e) => handleFieldChange('countryCode', e.target.value)}
                            placeholder="US"
                        />
                        <SimpleTooltip content={t('sms.routes.fields.priority')} position="top">
                            <Label htmlFor="priority" className="text-left">
                                {t('sms.routes.fields.priority')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="priority"
                            type="number"
                            value={formData.priority || 100}
                            onChange={(e) => handleFieldChange('priority', parseInt(e.target.value) || 100)}
                        />

                        {/* Row 3: Primary Provider | Fallback Provider */}
                        <SimpleTooltip content={t('sms.routes.fields.provider')} position="top">
                            <Label htmlFor="providerId" required className="text-left">
                                {t('sms.routes.fields.provider')}
                            </Label>
                        </SimpleTooltip>
                        <Select
                            value={formData.providerId?.toString() || ''}
                            onValueChange={(value) => handleFieldChange('providerId', parseInt(value))}
                            options={providerOptions}
                            placeholder={t('sms.routes.selectProvider')}
                            searchable={true}
                        />
                        <SimpleTooltip content={t('sms.routes.fields.fallbackProvider')} position="top">
                            <Label htmlFor="fallbackProviderId" className="text-left">
                                {t('sms.routes.fields.fallbackProvider')}
                            </Label>
                        </SimpleTooltip>
                        <Select
                            value={formData.fallbackProviderId?.toString() || '0'}
                            onValueChange={(value) => handleFieldChange('fallbackProviderId', value === '0' ? undefined : parseInt(value))}
                            options={fallbackProviderOptions}
                            searchable={true}
                        />

                        {/* Row 4: Enabled */}
                        <SimpleTooltip content={t('sms.routes.fields.enabled')} position="top">
                            <Label htmlFor="enabled" className="text-left">
                                {t('sms.routes.fields.enabled')}
                            </Label>
                        </SimpleTooltip>
                        <Toggle
                            id="enabled"
                            value={formData.enabled}
                            onChange={(value) => handleFieldChange('enabled', value)}
                        />
                        <div /><div />
                    </div>
                </FormBox>
            </div>

            {/* Fixed Action Bar */}
            <FixedActionBar
                isEditMode={isEditMode}
                isSaving={isSaving}
                onSaveWithAction={handleSaveWithAction}
                useSaveDropdown={true}
                onCancel={handleCancel}
                onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
                hasChanges={hasChanges}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title={t('sms.routes.deleteTitle')}
                message={`${t('sms.routes.messages.confirmDelete')} (${formData.name})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
                isLoading={isDeleting}
            />
        </div>
    );
}
